import * as React from 'react';
import { createLocators } from '@u1/locators';
import clsx from 'clsx';

import { Typography } from '../Typography';
import useStyles from './Radio.styles';

export interface IRadioProps {
  /**
   * Text or React element displayed next to the radio button
   */
  label?: React.ReactNode;
  /**
   * Specifies the name of the input element.
   */
  name?: string;
  /**
   * Value held by input element
   */
  value: string;
  /**
   * Disables radio interactions
   */
  disabled?: boolean;
  /**
   * Sets 'checked' state of the input
   */
  checked?: boolean;
  /**
   * On change callback
   */
  onChange?: (value: string) => void;
  /**
   * Escape hatch to add styles to the radio wrapper
   */
  _UNSAFE_classname?: string;
}

export const radioButtonLocators = createLocators({
  name: 'Radio',
  componentLocators: [
    'label',
    'input',
    'radio',
    'radioWrap',
    'innerCircle',
    'label',
  ],
});
const { locators } = radioButtonLocators();

const Radio: React.FC<IRadioProps> = ({
  name,
  label,
  value,
  disabled,
  checked,
  onChange,
  _UNSAFE_classname,
}) => {
  const classes = useStyles();

  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked && onChange) {
      onChange(value);
    }
  };

  return (
    <label
      {...locators.label()}
      className={clsx(_UNSAFE_classname, {
        [classes.root]: true,
        [classes.disabled]: disabled,
        [classes.checked]: checked,
      })}
    >
      <input
        {...locators.input()}
        type="radio"
        name={name}
        value={value}
        onChange={handleOnChange}
        checked={checked}
        disabled={disabled}
        className={classes.input}
      />

      <div {...locators.radioWrap} className={classes.radioWrap}>
        <div {...locators.label} className={clsx(classes.radio)}>
          <div {...locators.innerCircle} className={classes.innerCircle}></div>
        </div>
      </div>

      {label ? (
        <Typography
          locatorsOverrides={locators.label()}
          className={classes.label}
          as="span"
        >
          {label}
        </Typography>
      ) : null}
    </label>
  );
};

export default Radio;
